import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PageContainer from "@/components/layouts/PageContainer";
import useLoginStore from "@/stores/loginStore";
import { Heading } from "@/components/heading";
import { Text } from "@/components/text";
import { PlusIcon } from "@heroicons/react/20/solid";
import { Button } from "@/components/button";
import { Input, InputGroup } from "@/components/input";
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  XMarkIcon,
} from "@heroicons/react/16/solid";
import { useQueryParameters } from "@/hooks/useQueryParameters";
import { PaginationComponent } from "@/components/PaginationComponent";
import {
  useQueryGetUserList,
  type IUserListParams,
} from "@/apis/users/getUserList";
import { getPaginationInfo } from "@/types/pagination";
import UserListTable from "./UserListTable";
import ActiveFilters, { type Filter } from "@/components/ActiveFilters";
import FilteringUserListModal, {
  type FilterState,
} from "./components/FilteringUserListModal";
import { useQueryGetGroupList } from "@/apis/groups/getGroupList";

type SortField = "grade" | "name" | "school" | null;
type SortOrder = "asc" | "desc";

const UserListPage: React.FC = () => {
  const { clearToken } = useLoginStore();
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  // Configure query parameters with localStorage persistence
  const { params, setParam, setParams } = useQueryParameters({
    page: {
      defaultValue: 1,
      removeWhenDefault: true,
    },
    search: {
      defaultValue: "",
      removeWhenDefault: true,
    },
    gradeFilter: {
      defaultValue: "all",
      removeWhenDefault: true,
      persist: true,
    },
    groupFilter: {
      defaultValue: "all",
      removeWhenDefault: true,
      persist: true,
    },
    sort: {
      defaultValue: null as SortField,
      removeWhenDefault: true,
      persist: true,
    },
    order: {
      defaultValue: "desc" as SortOrder,
      removeWhenDefault: true,
      persist: true,
    },
    limit: {
      defaultValue: 10,
      removeWhenDefault: true,
    },
  }, {
    storageKey: "userListSettings",
  });

  // Sync search input with URL param
  useEffect(() => {
    setSearchInput(params.search);
  }, [params.search]);

  // Get group list for filter display
  const { data: groupsData } = useQueryGetGroupList({ limit: 100 });

  // API 호출
  const { data, isLoading, error } = useQueryGetUserList({
    page: params.page,
    limit: params.limit,
    role: "student",
    search: params.search || undefined,
    grade: params.gradeFilter === "all" ? undefined : parseInt(params.gradeFilter),
    group_id: params.groupFilter === "all" ? undefined : params.groupFilter,
    sort: params.sort || undefined,
    sort_order: params.sort ? params.order : undefined,
  });

  // 검색 처리
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setParams({
      search: searchInput,
      page: 1,
    });
  };

  // Handle sorting
  const handleSort = (field: SortField) => {
    if (params.sort === field) {
      if (params.order === "asc") {
        // asc → desc
        setParam("order", "desc");
      } else {
        // desc → 정렬 해제
        setParams({
          sort: null,
          order: "desc",
        });
      }
    } else {
      // New field clicked, set to ascending
      setParams({
        sort: field,
        order: "asc",
        page: 1,
      });
    }
  };

  // Filter modal handlers
  const handleOpenFilterModal = () => {
    setIsFilterModalOpen(true);
  };

  const handleCloseFilterModal = () => {
    setIsFilterModalOpen(false);
  };

  const handleApplyFilters = (newFilters: FilterState) => {
    setParams({
      gradeFilter: newFilters.gradeFilter,
      groupFilter: newFilters.groupFilter,
      page: 1,
    });
  };

  const handleRemoveFilter = (filterKey: string) => {
    if (filterKey === "gradeFilter") {
      setParams({
        gradeFilter: "all",
        page: 1,
      });
    } else if (filterKey === "groupFilter") {
      setParams({
        groupFilter: "all",
        page: 1,
      });
    }
  };

  const handleClearAllFilters = () => {
    setParams({
      gradeFilter: "all",
      groupFilter: "all",
      page: 1,
    });
  };

  // Get group name by ID
  const getGroupName = (groupId: string) => {
    const group = groupsData?.items?.find((g) => g.id === groupId);
    return group?.name || "Unknown Group";
  };

  // Get active filters for display
  const getActiveFilters = (): Filter[] => {
    const activeFilters: Filter[] = [];

    if (params.gradeFilter !== "all") {
      activeFilters.push({
        key: "gradeFilter",
        label: `Grade ${params.gradeFilter}`,
        value: params.gradeFilter,
      });
    }

    if (params.groupFilter !== "all") {
      activeFilters.push({
        key: "groupFilter",
        label: getGroupName(params.groupFilter),
        value: params.groupFilter,
      });
    }

    return activeFilters;
  };

  // 페이지네이션 정보
  const paginationInfo = data ? getPaginationInfo(data) : null;

  // 페이지 이동 핸들러
  const handlePageChange = (page: number) => {
    setParam("page", page);
  };

  // 학생 추가 페이지로 이동
  const handleAddStudent = () => {
    navigate("/users/create");
  };

  return (
    <PageContainer>
      <div className="space-y-6">
        <Heading>Student List</Heading>
        <Text className="text-zinc-500">
          View all students registered at the academy. Use the search to find
          specific students.
        </Text>

        <div className="flex justify-between mt-4">
          <div className="flex items-center gap-3">
            <Button onClick={handleAddStudent}>
              <PlusIcon />
              Add Student
            </Button>

            <Button onClick={handleOpenFilterModal}>
              <FunnelIcon />
              Filter
            </Button>
          </div>

          <form onSubmit={handleSearch} className="flex items-center">
            <InputGroup>
              <MagnifyingGlassIcon />
              <Input
                name="search"
                placeholder="name, email, phone"
                aria-label="Search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </InputGroup>
            <Button type="submit" className="ml-2">
              Search
            </Button>
          </form>
        </div>

        {/* Active Filters */}
        <ActiveFilters
          filters={getActiveFilters()}
          onRemoveFilter={handleRemoveFilter}
          onClearAll={handleClearAllFilters}
        />

        {/* User List Table Component */}
        <UserListTable
          users={data?.items || []}
          isLoading={isLoading}
          error={error as Error | null}
          sortField={params.sort}
          sortOrder={params.order}
          onSort={handleSort}
        />

        {/* Pagination */}
        {paginationInfo && (
          <div className="mt-8 flex justify-center">
            <PaginationComponent
              currentPage={paginationInfo.currentPage}
              totalPages={paginationInfo.totalPages}
              hasNextPage={paginationInfo.hasNextPage}
              hasPrevPage={paginationInfo.hasPrevPage}
              onPageChange={handlePageChange}
            />
          </div>
        )}

        {/* Filter Modal */}
        <FilteringUserListModal
          open={isFilterModalOpen}
          onClose={handleCloseFilterModal}
          onApply={handleApplyFilters}
          currentFilters={{
            gradeFilter: params.gradeFilter as "all" | "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9" | "10" | "11" | "12",
            groupFilter: params.groupFilter as "all" | string,
          }}
        />
      </div>
    </PageContainer>
  );
};

export default UserListPage;
